import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit,
  Loader2,
  Shuffle,
  ArrowRight,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Eye,
  BookTemplate,
  Copy,
  Database
} from 'lucide-react';

const RULE_TYPES = [
  { value: 'rename', label: 'Rename Column', description: 'Rename a source column to a new name' },
  { value: 'default', label: 'Default Value', description: 'Set a default if column is empty' },
  { value: 'type_cast', label: 'Type Cast', description: 'Convert column to number, integer, or string' },
  { value: 'value_map', label: 'Value Map', description: 'Map specific values to new values' },
  { value: 'filter', label: 'Filter Rows', description: 'Keep or remove rows based on conditions' },
  { value: 'exclude_column', label: 'Exclude Column', description: 'Remove a column from output' },
];

const TransformationsPage = () => {
  const [transformations, setTransformations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateJobId, setTemplateJobId] = useState('');
  const [templateName, setTemplateName] = useState('');

  const [form, setForm] = useState({
    name: '',
    job_id: '',
    rules: []
  });

  const [newRule, setNewRule] = useState({
    type: 'rename',
    source_column: '',
    target_column: '',
    params: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transRes, jobsRes, templatesRes] = await Promise.all([
        apiService.getTransformations(),
        apiService.getJobs(),
        apiService.getTransformTemplates()
      ]);
      setTransformations(transRes.data);
      setJobs(jobsRes.data.filter(j => j.job_type === 'fetch'));
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (trans = null) => {
    if (trans) {
      setEditingRule(trans);
      setForm({ name: trans.name, job_id: trans.job_id || '', rules: trans.rules || [] });
    } else {
      setEditingRule(null);
      setForm({ name: '', job_id: '', rules: [] });
    }
    setNewRule({ type: 'rename', source_column: '', target_column: '', params: {} });
    setDialogOpen(true);
  };

  const addRule = () => {
    if (!newRule.source_column && newRule.type !== 'filter') {
      toast.error('Source column is required');
      return;
    }
    setForm(prev => ({ ...prev, rules: [...prev.rules, { ...newRule }] }));
    setNewRule({ type: 'rename', source_column: '', target_column: '', params: {} });
  };

  const removeRule = (index) => {
    setForm(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
  };

  const moveRule = (index, direction) => {
    const newRules = [...form.rules];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newRules.length) return;
    [newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]];
    setForm(prev => ({ ...prev, rules: newRules }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (form.rules.length === 0) {
      toast.error('Add at least one transformation rule');
      return;
    }

    try {
      if (editingRule) {
        await apiService.updateTransformation(editingRule.id, form);
        toast.success('Transformation updated');
      } else {
        await apiService.createTransformation(form);
        toast.success('Transformation created');
      }
      loadData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.deleteTransformation(id);
      toast.success('Transformation deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (trans) => {
    try {
      await apiService.updateTransformation(trans.id, { is_active: !trans.is_active });
      toast.success(trans.is_active ? 'Disabled' : 'Enabled');
      loadData();
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  const openTemplateDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateJobId('');
    setTemplateDialogOpen(true);
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await apiService.applyTransformTemplate(selectedTemplate.id, {
        name: templateName || selectedTemplate.name,
        job_id: templateJobId || null
      });
      toast.success(`Template "${selectedTemplate.name}" applied`);
      setTemplateDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const handlePreview = async () => {
    const sampleData = [
      { NAME: "Cash", PARENT: "Current Assets", OPENINGBALANCE: "50000.00", CLOSINGBALANCE: "75000.00", _tally_guid: "abc-123" },
      { NAME: "Bank", PARENT: "Current Assets", OPENINGBALANCE: "", CLOSINGBALANCE: "120000.00", _tally_guid: "def-456" },
      { NAME: "Sales", PARENT: "Revenue", OPENINGBALANCE: "0", CLOSINGBALANCE: "500000.00", _tally_guid: "ghi-789" },
    ];

    try {
      const res = await apiService.previewTransformation({ sample_data: sampleData, rules: form.rules });
      setPreviewResult(res.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Preview failed');
    }
  };

  const getRuleLabel = (rule) => {
    const typeDef = RULE_TYPES.find(r => r.value === rule.type);
    return typeDef?.label || rule.type;
  };

  const getRuleDescription = (rule) => {
    switch (rule.type) {
      case 'rename': return `${rule.source_column} -> ${rule.target_column}`;
      case 'default': return `${rule.source_column} = "${rule.params?.value || ''}" if empty`;
      case 'type_cast': return `${rule.source_column} as ${rule.params?.cast_to || 'string'}`;
      case 'value_map': return `${rule.source_column}: ${Object.keys(rule.params?.mappings || {}).length} mappings`;
      case 'filter': return `${rule.source_column} ${rule.params?.operator || 'equals'} "${rule.params?.value || ''}"`;
      case 'exclude_column': return `Remove ${rule.source_column}`;
      default: return '';
    }
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job?.name || 'Unlinked';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="transformations-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Data Transformations</h1>
          <p className="page-subtitle">Define rules to transform fetched Tally data before database insertion</p>
        </div>
        <Button className="bg-teal-700 hover:bg-teal-600" onClick={() => openDialog()} data-testid="create-transform-btn">
          <Plus className="h-4 w-4 mr-2" />New Transformation
        </Button>
      </div>

      {/* Template Library */}
      {templates.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <BookTemplate className="h-4 w-4" />Template Library
          </h2>
          <div className="grid grid-cols-4 gap-3">
            {templates.map((t) => (
              <Card key={t.id} className="cursor-pointer hover:border-teal-300 transition-colors" data-testid={`template-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-sm">{t.name}</h3>
                    <Badge variant="outline" className="text-xs shrink-0 ml-2">{t.data_type}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{t.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{t.rules_count} rules</span>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openTemplateDialog(t)} data-testid={`apply-template-${t.id}`}>
                      <Copy className="h-3 w-3" />Use
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Name</TableHead>
                <TableHead className="table-cell">Linked Job</TableHead>
                <TableHead className="table-cell">Rules</TableHead>
                <TableHead className="table-cell">Active</TableHead>
                <TableHead className="table-cell text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transformations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    <Shuffle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No transformations defined</p>
                    <p className="text-sm">Add transformation rules for your fetch jobs</p>
                  </TableCell>
                </TableRow>
              ) : (
                transformations.map((trans) => (
                  <TableRow key={trans.id} className="table-row" data-testid={`transform-row-${trans.id}`}>
                    <TableCell className="table-cell font-medium">{trans.name}</TableCell>
                    <TableCell className="table-cell text-sm text-slate-600">
                      {trans.job_id ? getJobName(trans.job_id) : <span className="text-slate-400">None</span>}
                    </TableCell>
                    <TableCell className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {trans.rules.slice(0, 3).map((rule, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {getRuleLabel(rule)}
                          </Badge>
                        ))}
                        {trans.rules.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{trans.rules.length - 3} more</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Switch checked={trans.is_active} onCheckedChange={() => handleToggle(trans)} data-testid={`toggle-transform-${trans.id}`} />
                    </TableCell>
                    <TableCell className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openDialog(trans)} data-testid={`edit-transform-${trans.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(trans.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-transform-${trans.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Transformation' : 'New Transformation'}</DialogTitle>
            <DialogDescription>Define rules that apply to fetched data before insertion into the database</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Ledger Cleanup" data-testid="transform-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Link to Fetch Job</Label>
                <Select value={form.job_id} onValueChange={(value) => setForm({ ...form, job_id: value })}>
                  <SelectTrigger data-testid="transform-job-select">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Existing Rules */}
            {form.rules.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Rules ({form.rules.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-1">
                    {form.rules.map((rule, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-100 text-sm">
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveRule(index, -1)} disabled={index === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button onClick={() => moveRule(index, 1)} disabled={index === form.rules.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">{getRuleLabel(rule)}</Badge>
                        <span className="text-slate-600 flex-1 truncate">{getRuleDescription(rule)}</span>
                        <button onClick={() => removeRule(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add New Rule */}
            <Card className="border-dashed">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm">Add Rule</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Rule Type</Label>
                    <Select value={newRule.type} onValueChange={(value) => setNewRule({ ...newRule, type: value, params: {} })}>
                      <SelectTrigger className="h-9" data-testid="new-rule-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RULE_TYPES.map((rt) => (
                          <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Source Column</Label>
                    <Input className="h-9" value={newRule.source_column} onChange={(e) => setNewRule({ ...newRule, source_column: e.target.value })} placeholder="e.g., NAME" data-testid="new-rule-source-input" />
                  </div>
                  {newRule.type === 'rename' && (
                    <div className="space-y-1">
                      <Label className="text-xs">New Name</Label>
                      <Input className="h-9" value={newRule.target_column} onChange={(e) => setNewRule({ ...newRule, target_column: e.target.value })} placeholder="e.g., ledger_name" />
                    </div>
                  )}
                  {newRule.type === 'default' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Default Value</Label>
                      <Input className="h-9" value={newRule.params?.value || ''} onChange={(e) => setNewRule({ ...newRule, params: { value: e.target.value } })} placeholder="e.g., 0" />
                    </div>
                  )}
                  {newRule.type === 'type_cast' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Cast To</Label>
                      <Select value={newRule.params?.cast_to || 'string'} onValueChange={(value) => setNewRule({ ...newRule, params: { cast_to: value } })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="number">Number (float)</SelectItem>
                          <SelectItem value="integer">Integer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newRule.type === 'filter' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Operator & Value</Label>
                      <div className="flex gap-1">
                        <Select value={newRule.params?.operator || 'equals'} onValueChange={(value) => setNewRule({ ...newRule, params: { ...newRule.params, operator: value } })}>
                          <SelectTrigger className="h-9 w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">Equals</SelectItem>
                            <SelectItem value="not_equals">Not Equals</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                            <SelectItem value="not_empty">Not Empty</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input className="h-9 flex-1" value={newRule.params?.value || ''} onChange={(e) => setNewRule({ ...newRule, params: { ...newRule.params, value: e.target.value } })} placeholder="value" />
                      </div>
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={addRule} data-testid="add-rule-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Rule
                </Button>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={handlePreview} disabled={form.rules.length === 0} data-testid="preview-transform-btn">
              <Eye className="h-4 w-4 mr-2" />Preview
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button className="bg-teal-700 hover:bg-teal-600" onClick={handleSave} data-testid="save-transform-btn">
                {editingRule ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transformation Preview</DialogTitle>
            <DialogDescription>
              {previewResult && `${previewResult.original_count} records in, ${previewResult.transformed_count} records out`}
            </DialogDescription>
          </DialogHeader>
          {previewResult && (
            <div className="max-h-[50vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {previewResult.transformed.length > 0 && Object.keys(previewResult.transformed[0]).map((key) => (
                      <TableHead key={key} className="text-xs">{key}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewResult.transformed.map((row, i) => (
                    <TableRow key={i}>
                      {Object.values(row).map((val, j) => (
                        <TableCell key={j} className="text-xs">{String(val ?? '')}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Template: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Transformation Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={selectedTemplate?.name}
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Link to Fetch Job (optional)</Label>
              <Select value={templateJobId} onValueChange={setTemplateJobId}>
                <SelectTrigger data-testid="template-job-select">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-slate-500 mb-1">Includes {selectedTemplate?.rules_count} rules:</p>
              <p className="text-xs text-slate-600">Rename fields, cast types, set defaults for {selectedTemplate?.data_type} data</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-600" onClick={handleApplyTemplate} data-testid="confirm-apply-template-btn">
              Apply Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransformationsPage;
